
/**
 * OverlayEditor — deterministic 4-zone branded overlay editor.
 *
 *   Takes any source image (uploaded, AI-generated, or brand asset),
 *   exposes the four brand-locked overlay zones (eyebrow / title /
 *   subtitle / URL pill, plus brand-logo toggle) as plain text inputs,
 *   and renders the finished poster via the backend's
 *   POST /api/content-calendar/visuals/compose-overlays endpoint.
 *
 *   Text is composited server-side by PIL — pixel-perfect, no LLM
 *   guesswork. The "change Form to from / add our logo / add website"
 *   workflow lives here.
 */
import React, { useEffect, useState } from "react";
import { Type, Hash, Globe, Image as ImageIcon, RefreshCw, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OverlayEditor({
  sourceUrl,
  format = "ig-square",
  draftId,
  apiToken,
  initialTitle = "",
  initialSubtitle = "",
  initialEyebrow = "",
  onRendered,        // (url) => void  — called when a poster is rendered
}) {
  const [eyebrow,  setEyebrow]  = useState(initialEyebrow);
  const [title,    setTitle]    = useState(initialTitle);
  const [subtitle, setSubtitle] = useState(initialSubtitle);
  const [url,      setUrl]      = useState("hidroverse.co.za");
  const [position, setPosition] = useState("bottom"); // 'bottom' | 'top' | 'center'
  const [scrim,    setScrim]    = useState(true);
  const [showLogo, setShowLogo] = useState(true);

  const [rendering, setRendering] = useState(false);
  const [error,     setError]     = useState("");
  const [output,    setOutput]    = useState(null);

  useEffect(() => {
    // Defer the field reset through a microtask so the strict
    // react-hooks/set-state-in-effect rule doesn't trip on the
    // synchronous setState chain. Functionally identical.
    const t = window.setTimeout(() => {
      setEyebrow(initialEyebrow);
      setTitle(initialTitle);
      setSubtitle(initialSubtitle);
      setOutput(null);
    }, 0);
    return () => window.clearTimeout(t);
  }, [sourceUrl, initialEyebrow, initialTitle, initialSubtitle]);

  const render = async () => {
    if (!sourceUrl) {
      setError("Pick a source image first (the canvas the overlays sit on).");
      return;
    }
    setError("");
    setRendering(true);
    try {
      const r = await fetch(`${API}/api/content-calendar/visuals/compose-overlays`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          source_url: sourceUrl,
          format,
          draft_id: draftId,
          zones: {
            eyebrow:  eyebrow  || null,
            title:    title    || null,
            subtitle: subtitle || null,
            url:      url      || null,
            title_position: position,
            scrim, show_logo: showLogo,
          },
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Overlay render failed");
      setOutput(data);
      if (onRendered) onRendered(data.url);
    } catch (e) {
      setError(e.message);
    } finally {
      setRendering(false);
    }
  };

  return (
    <div data-testid={`vs-overlay-editor-${draftId}`}
         style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }}>
      {/* ── Left: text zones + toggles ─────────────────────── */}
      <div>
        <div className="font-mono uppercase mb-2"
             style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                      color: "#0E7A82", fontWeight: 700 }}>
          Brand-locked overlay zones
        </div>
        <p style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.65)",
                    marginBottom: 12, lineHeight: 1.5 }}>
          Pure text editing — no AI guessing. Brand fonts, colours and
          position bands are locked. Type the copy; the server
          composites it pixel-perfect onto your source image.
        </p>

        <ZoneField label="Eyebrow (small caps)" icon={<Type size={11} />}
                   value={eyebrow} onChange={setEyebrow}
                   placeholder="INSIGHT · CASE STUDY · WHAT WE DID"
                   testid={`vs-overlay-eyebrow-${draftId}`} />
        <ZoneField label="Title (1–3 short lines)" icon={<Type size={13} />}
                   value={title} onChange={setTitle}
                   placeholder="From abstraction to supply"
                   multiline rows={2}
                   testid={`vs-overlay-title-${draftId}`} />
        <ZoneField label="Subtitle (optional blurb)" icon={<Type size={11} />}
                   value={subtitle} onChange={setSubtitle}
                   placeholder="Scheduled pull at Thornybush — pump, motor and column inspection."
                   multiline rows={2}
                   testid={`vs-overlay-subtitle-${draftId}`} />
        <ZoneField label="URL pill" icon={<Globe size={11} />}
                   value={url} onChange={setUrl}
                   placeholder="hidroverse.co.za"
                   testid={`vs-overlay-url-${draftId}`} />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          <SegBtn options={[
              { v: "bottom", label: "Title bottom" },
              { v: "center", label: "Title center" },
              { v: "top",    label: "Title top" },
            ]} value={position} onChange={setPosition}
            testid={`vs-overlay-position-${draftId}`} />
          <Toggle label="Dark scrim"      on={scrim}    onClick={() => setScrim(s => !s)}
                  testid={`vs-overlay-scrim-${draftId}`} />
          <Toggle label="Brand logo"      on={showLogo} onClick={() => setShowLogo(s => !s)}
                  testid={`vs-overlay-logo-${draftId}`} />
        </div>

        <button
          type="button"
          onClick={render}
          disabled={rendering || !sourceUrl}
          data-testid={`vs-overlay-render-${draftId}`}
          style={{
            marginTop: 14,
            padding: "9px 16px",
            background: sourceUrl ? "#1FB6C1" : "rgba(31,182,193,0.25)",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 999,
            fontSize: "0.85rem",
            fontWeight: 700,
            cursor: sourceUrl ? "pointer" : "not-allowed",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
          {rendering ? <RefreshCw size={13} className="hv-spin" /> : <ImageIcon size={13} />}
          {rendering ? "Rendering…" : "Render overlay"}
        </button>

        {error && (
          <div style={{ marginTop: 10, fontSize: "0.8rem", color: "#B85C38" }}>
            {error}
          </div>
        )}
      </div>

      {/* ── Right: preview ─────────────────────────────────── */}
      <div>
        <div className="font-mono uppercase mb-2"
             style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                      color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
          Preview
        </div>
        {output ? (
          <div>
            <img src={output.url} alt="overlay-poster"
                 style={{ maxWidth: "100%", borderRadius: 8,
                          border: "1px solid rgba(12,42,64,0.10)",
                          boxShadow: "0 10px 30px -15px rgba(12,42,64,0.30)" }}
                 data-testid={`vs-overlay-output-${draftId}`} />
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: "0.74rem", color: "rgba(12,42,64,0.6)" }}>
              <Check size={11} style={{ color: "#0E7A82" }} />
              Rendered pixel-perfect by the brand compositor.
              {output.brand_logo_attached && " · Brand logo composited."}
            </div>
          </div>
        ) : sourceUrl ? (
          <div>
            <img src={sourceUrl} alt="source-canvas"
                 style={{ maxWidth: "100%", borderRadius: 8,
                          border: "1px solid rgba(12,42,64,0.10)",
                          opacity: 0.72 }} />
            <div style={{ marginTop: 8, fontSize: "0.74rem", color: "rgba(12,42,64,0.55)" }}>
              Source image — click <strong>Render overlay</strong> to composite.
            </div>
          </div>
        ) : (
          <div style={{
            padding: "30px 20px", background: "#FCFCFA",
            border: "1px dashed rgba(12,42,64,0.18)", borderRadius: 8,
            color: "rgba(12,42,64,0.55)", fontSize: "0.85rem", textAlign: "center",
          }}>
            Select a source image first (Generate, Upload, or pick from the gallery).
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Small inputs / toggles ────────────────────────────────── */
function ZoneField({ label, value, onChange, placeholder, multiline = false, rows = 1, icon, testid }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="font-mono uppercase mb-1"
           style={{ fontSize: "0.56rem", letterSpacing: "0.20em",
                    color: "rgba(12,42,64,0.55)", fontWeight: 700,
                    display: "inline-flex", alignItems: "center", gap: 5 }}>
        {icon} {label}
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          data-testid={testid}
          style={{
            width: "100%", padding: "8px 10px",
            background: "#FFFFFF",
            border: "1px solid rgba(12,42,64,0.16)",
            borderRadius: 6,
            fontSize: "0.86rem", color: "#0C2A40",
            fontFamily: "inherit", lineHeight: 1.45,
            resize: "vertical",
          }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          style={{
            width: "100%", padding: "8px 10px",
            background: "#FFFFFF",
            border: "1px solid rgba(12,42,64,0.16)",
            borderRadius: 6,
            fontSize: "0.86rem", color: "#0C2A40",
          }}
        />
      )}
    </div>
  );
}

function SegBtn({ options, value, onChange, testid }) {
  return (
    <div style={{ display: "inline-flex", borderRadius: 999, overflow: "hidden",
                  border: "1px solid rgba(12,42,64,0.14)" }} data-testid={testid}>
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button key={o.v} type="button" onClick={() => onChange(o.v)}
                  style={{
                    padding: "5px 10px",
                    background: active ? "rgba(31,182,193,0.18)" : "#FFFFFF",
                    color:      active ? "#0E7A82" : "rgba(12,42,64,0.65)",
                    border: "none",
                    fontSize: "0.74rem",
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                  }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({ label, on, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            style={{
              padding: "5px 12px",
              background: on ? "rgba(31,182,193,0.18)" : "#FFFFFF",
              color:      on ? "#0E7A82" : "rgba(12,42,64,0.65)",
              border: `1px solid ${on ? "rgba(31,182,193,0.45)" : "rgba(12,42,64,0.14)"}`,
              borderRadius: 999,
              fontSize: "0.74rem",
              fontWeight: on ? 700 : 500,
              cursor: "pointer",
            }}>
      {on ? "✓ " : ""}{label}
    </button>
  );
}
